// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

import { HelpCircle, Printer } from "lucide-react";
import { useState } from "react";

import type { BridgeTransport } from "@/bridge/types";
import { DeviceView } from "@/features/device-view";
import { HaltReport } from "@/features/halt-report";
import { SearchBox } from "@/features/search-box";
import { SeatChip } from "@/features/seat-chip";
import { readSeatChipFromLocation } from "@/features/seat-chips";
import { useDeviceQuery } from "@/features/use-device-query";
import { VerifiedBadge } from "@/features/verified-badge";
import { readVerifiedBadgeFromLocation } from "@/features/verified-badges";
import { cn } from "@/lib/utils";
import { NCActionFooter, NCActionFooterBar } from "@/nc/action-footer";
import { NCGuidedTour, type NCGuidedTourStep } from "@/nc/guided-tour";
import { NCPageHeader } from "@/nc/page-header";
import { NCStagePanel } from "@/nc/stage-panel";
import { Button } from "@/primitives/button";

export interface AppProps {
  /** Injected bridge transport (real Zoho transport in production; mock in dev). */
  transport: BridgeTransport;
}

const TOUR_STORAGE_KEY = "au0009_tour_seen_v1";

const TOUR_STEPS: readonly NCGuidedTourStep[] = [
  {
    targetSelector: ".au0009-search-bar",
    title: "Look up a device",
    body: "Enter a serial number or software ID here, then select Show status to see its identity and latest firmware update.",
  },
];

function tourSeen(): boolean {
  try {
    return window.localStorage.getItem(TOUR_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markTourSeen(): void {
  try {
    window.localStorage.setItem(TOUR_STORAGE_KEY, "1");
  } catch {
    // storage unavailable — the tour simply replays each load
  }
}

export function App({ transport }: AppProps) {
  const query = useDeviceQuery(transport);
  const showProgress = true;
  const outcome = query.outcome;
  const [tourOpen, setTourOpen] = useState<boolean>(() => !tourSeen());
  // Header seat chip (#64): the `is-demo-seat` / `is-educational-seat` flags,
  // read ONCE on mount (synchronously, so the chip is there on first render)
  // and held as one session-level value. Display-only and untrusted (see the
  // Trust rule in `features/seat-chips.ts`): it never reaches the orchestrator,
  // per-stage progress or halt-and-report. A USER fact, independent of the
  // device `isDemo` (ADR-008).
  const [seatChip] = useState(() => readSeatChipFromLocation(() => window.location.search));
  // Header verified badge (#68, trial): `is-pilot-seat` / `is-standard-seat`, read
  // the same way and under the same Trust rule (`features/verified-badges.ts`).
  const [verifiedBadge] = useState(() => readVerifiedBadgeFromLocation(() => window.location.search));
  const titleAdornment =
    verifiedBadge !== null || seatChip !== null ? (
      <>
        {verifiedBadge !== null ? <VerifiedBadge badgeKey={verifiedBadge} /> : null}
        {seatChip !== null ? <SeatChip chipKey={seatChip} /> : null}
      </>
    ) : null;

  const results =
    query.phase === "done" && outcome ? (
      <section aria-live="polite">
        {outcome.kind === "resolved_shown" ? (
          <DeviceView
            identity={outcome.identity}
            queriedUpdaterAUs={outcome.queriedUpdaterAUs}
            {...(outcome.operation ? { operation: outcome.operation } : {})}
            {...(outcome.offlineWinner
              ? { offlineWinner: outcome.offlineWinner }
              : {})}
            {...(outcome.offlineAugment
              ? { offlineAugment: outcome.offlineAugment }
              : {})}
            {...(outcome.offlineAttributionList
              ? { offlineAttributionList: outcome.offlineAttributionList }
              : {})}
          />
        ) : outcome.kind === "resolved_no_operation" ? (
          <DeviceView
            identity={outcome.identity}
            queriedUpdaterAUs={outcome.queriedUpdaterAUs}
          />
        ) : (
          <HaltReport outcome={outcome} />
        )}
      </section>
    ) : null;

  return (
    // No-chrome: only the embedded content area. Left-anchored, not centered
    // (layouts/widget-content-area.md). The search action lives in the
    // bottom floating action bar (layouts/floating-action-bar.md), so it
    // stays reachable while the result column scrolls; pb-40 keeps content
    // clear of that fixed footer.
    //
    // `print:block print:min-h-0` overrides flex + min-h-full for print:
    // Chromium cannot fragment a flex container's own box across print
    // pages (a flex item is one unbreakable fragment), so once the results
    // column grows past one page, the whole flex box gets pushed onto the
    // NEXT page, leaving the first page blank. Plain block flow paginates
    // normally between its block-level children. Same reasoning applies to
    // the stage-panel grid and the results flex-col below.
    <main className="flex min-h-full w-full flex-col bg-background p-nc-6 pb-40 text-foreground print:block print:min-h-0">
      {/* Plain block wrapper, not flex: NCPageHeader's own root <header> is a
          block element and needs its parent to NOT be a flex container, or
          it shrinks to its title/description's own content width instead of
          filling this row — which left its bottom divider looking "half"
          (only as wide as the title text) instead of spanning the full row
          up to the action buttons, regardless of which maxWidth is active.
          `actions` renders the print/help buttons INSIDE NCPageHeader's own
          justify-between row instead of as an external flex sibling. */}
      <div
        className={cn("w-full print:hidden mx-auto", showProgress && "mx-auto")}
      >
        <NCPageHeader
          title="Show individual device information"
          description="Look up a device by serial number or software ID to see its identity and last firmware update operation."
          {...(titleAdornment !== null ? { titleAdornment } : {})}
          actions={
            <>
              {/* Print, not export (ADR-001). The browser's own print path only.
                  `title` gives a native hover tooltip — no Tooltip primitive
                  exists in this widget yet and this is a two-icon action row,
                  not worth a new dependency for; `aria-label` already carries
                  the same text for assistive tech, so `title` is purely the
                  sighted-hover affordance. */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Print"
                title="Print"
                onClick={() => window.print()}
                className="print:hidden"
              >
                <Printer className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Replay guided tour"
                title="Replay guided tour"
                onClick={() => setTourOpen(true)}
              >
                <HelpCircle className="size-4" aria-hidden />
              </Button>
            </>
          }
        />
      </div>

      {showProgress ? (
        // Stage panel layout (layouts/stage-panel-layout.md): a sticky side
        // column for the per-stage progress list, sized as a clamped
        // percentage rather than a one-off fixed width, at the canonical
        // --spacing-nc-6 gap from the main content column. This two-column
        // split only exists once there's something to show in it (Structure
        // #2) — it never renders during the idle state above.
        <div className="mx-auto mt-nc-6 grid w-full grid-cols-[minmax(0,1fr)_clamp(220px,30%,320px)] gap-nc-6 max-lg:grid-cols-1 print:block">
          <div className="flex min-w-0 flex-col gap-nc-6 print:block">{results}</div>

          <aside className="print:hidden lg:sticky lg:top-nc-6 lg:self-start">
            <NCStagePanel
              title="Lookup stages"
              stages={query.progress.map((stage) => ({
                id: stage.id,
                title: stage.label,
                status: stage.state,
                ...(stage.detail ? { detail: stage.detail } : {}),
              }))}
            />
          </aside>
        </div>
      ) : null}

      {/* Fixed width in every phase (idle, running, done) — the search bar
          holding it steady across the interaction, unlike the header/content
          row above, which is the one thing meant to widen once the stage
          panel appears (layouts/stage-panel-layout.md). */}
      <NCActionFooter className="print:hidden">
        <NCActionFooterBar className="au0009-search-bar">
          <SearchBox
            disabled={query.phase === "running"}
            onSearch={query.run}
            dropdownPosition="above"
          />
        </NCActionFooterBar>
      </NCActionFooter>

      <NCGuidedTour
        steps={TOUR_STEPS}
        open={tourOpen}
        onOpenChange={(open) => {
          setTourOpen(open);
          if (!open) markTourSeen();
        }}
      />
    </main>
  );
}
